import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import { cn } from '@/lib/utils';
import { estadoVencimiento } from '@/lib/tareas';
import { usePermisosTareas, useTareasLista } from '@/hooks/useTareas';
import type { CalendarTareasLayer } from '@/components/calendar/types';
import type { Tarea } from '@/types/tareas';
import { TareaDetailModal } from '@/pages/tareas/TareaDetailModal';

const STORAGE_CAPA = 'anik-agenda-capa-tareas';
const TAREAS_MAX_POR_RANGO = 200;
const CHIPS_VISIBLES = 3;
const FORMATO_DIA = 'yyyy-MM-dd';

function leerActiva(): boolean {
  try { return localStorage.getItem(STORAGE_CAPA) === '1'; } catch { return false; }
}

const ESTILO_CHIP = {
  vencida: 'bg-red-50 border-red-200 text-red-700',
  hoy: 'bg-amber-50 border-amber-200 text-amber-700',
  normal: 'bg-primary-50 border-primary-100 text-primary-700',
} as const;

function textoChip(t: Tarea): string {
  const f = new Date(t.fechaVencimiento as string);
  const conHora = f.getHours() !== 0 || f.getMinutes() !== 0;
  return conHora ? `${format(f, 'HH:mm')} ${t.titulo}` : t.titulo;
}

function ChipsTareasDia({ tareas, onAbrir }: { tareas: Tarea[]; onAbrir: (id: string) => void }) {
  const [expandido, setExpandido] = useState(false);
  const visibles = expandido ? tareas : tareas.slice(0, CHIPS_VISIBLES);
  const restantes = tareas.length - visibles.length;

  return (
    <div className="space-y-0.5">
      {visibles.map(t => {
        const estado = estadoVencimiento(t);
        return (
          <button
            key={t.id}
            onClick={e => { e.stopPropagation(); onAbrir(t.id); }}
            title={textoChip(t)}
            className={cn(
              'w-full text-left truncate rounded border px-1.5 py-0.5 text-[11px] leading-tight hover:brightness-95',
              ESTILO_CHIP[estado === 'vencida' ? 'vencida' : estado === 'hoy' ? 'hoy' : 'normal'],
            )}
          >
            {textoChip(t)}
          </button>
        );
      })}
      {restantes > 0 && (
        <button onClick={() => setExpandido(true)} className="text-[11px] text-primary-600 hover:underline px-1">
          +{restantes} más
        </button>
      )}
    </div>
  );
}

/**
 * Capa de tareas para la Agenda (vistas de día y semana): una franja "todo el día" con las tareas
 * abiertas que vencen cada día. Muestra las tareas que el usuario puede ver (las suyas; un admin ve
 * todas). Es opt-in y se recuerda en el navegador. Recepcionista no tiene acceso al módulo, así
 * que `capa` es undefined y la Agenda no muestra el botón.
 */
export function useCapaTareasAgenda(fecha: Date): { capa: CalendarTareasLayer | undefined; modal: ReactNode } {
  const { puedeUsar } = usePermisosTareas();
  const [activa, setActiva] = useState(leerActiva);
  const [abierta, setAbierta] = useState<string | null>(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_CAPA, activa ? '1' : '0'); } catch { /* almacenamiento no disponible */ }
  }, [activa]);

  const inicioSemana = startOfWeek(fecha, { weekStartsOn: 1 }).getTime();
  const rango = useMemo(() => ({
    vencimientoDesde: new Date(inicioSemana).toISOString(),
    vencimientoHasta: endOfWeek(new Date(inicioSemana), { weekStartsOn: 1 }).toISOString(),
  }), [inicioSemana]);

  const { data, isFetching } = useTareasLista(
    { ...rango, completadas: 'no', limit: TAREAS_MAX_POR_RANGO },
    puedeUsar && activa,
  );

  const porDia = useMemo(() => {
    const mapa = new Map<string, Tarea[]>();
    (data?.items ?? []).forEach(t => {
      if (!t.fechaVencimiento) return;
      const clave = format(new Date(t.fechaVencimiento), FORMATO_DIA);
      mapa.set(clave, [...(mapa.get(clave) ?? []), t]);
    });
    return mapa;
  }, [data]);

  const capa = useMemo<CalendarTareasLayer | undefined>(() => (puedeUsar ? {
    activa,
    onToggle: () => setActiva(v => !v),
    isLoading: isFetching,
    renderDia: (dia: Date) => <ChipsTareasDia tareas={porDia.get(format(dia, FORMATO_DIA)) ?? []} onAbrir={setAbierta} />,
  } : undefined), [puedeUsar, activa, isFetching, porDia]);

  const modal = puedeUsar
    ? <TareaDetailModal tareaId={abierta} onClose={() => setAbierta(null)} onAbrirTarea={setAbierta} />
    : null;

  return { capa, modal };
}
